import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import Modal from 'react-modal';
import cn from 'classnames';

import { Loader } from 'components/Loader';
import Rating from 'components/Rating';
import SocialCollectionsWidget from 'components/account/SocialCollectionsWidget';
import { addHeartToCart } from 'actions/hearts';
import { fetchProductDetail } from 'actions/productDetail';
import { constructMSAImageUrl, pluralize } from 'helpers';
import { toUSD } from 'helpers/NumberFormats';
import { getAsinFromProductUrl } from 'helpers/MyAccountUtils';
import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/components/account/collectionQuickview.scss';

const msaImageDimensions = { height: 600 };

const initialState = {
  dimensions: null,
  colorStock: null,
  selected: {}
};

export class CollectionQuickview extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  };

  state = initialState;

  componentDidMount() {
    const {
      item,
      fetchProductDetail
    } = this.props;

    const { productUrl, quantity } = item;

    // product is available, make productBundle call
    if (quantity) {
      const asin = getAsinFromProductUrl(productUrl);

      fetchProductDetail({ asin, includeOosSizing: true })
        .then(() => {
          const {
            product: {
              colorId,
              detail: { sizing }
            }
          } = this.props;
          const { dimensions, stockData } = sizing;

          // filter specific color from stockData
          const colorStock = stockData.filter(({ color }) => color === colorId);

          // this product has no dimensions
          if (dimensions.length === 0) {
            const { onHand, id } = colorStock[0];
            const oos = !!(onHand <= 0);

            this.setState({
              isDimsSelected: true,
              stockId: id,
              oos
            });
          }

          this.setState({
            dimensions,
            colorStock
          });
        });
    }
  }

  onDimensionChange = ({ target }) => {
    const { name, value } = target;
    const { selected } = this.state;
    if (value) {
      this.setState({
        selected: {
          ...selected,
          [name]: value
        }
      }, this.onDimensionChangeCallback);
    } else {
      const newSelected = { ...selected };
      delete newSelected[name];

      this.setState({
        selected: { ...newSelected }
      }, this.onDimensionChangeCallback);
    }
  }

  onDimensionChangeCallback = () => {
    const { selected, dimensions } = this.state;
    const sKeys = Object.keys(selected);
    const isDimsSelected = sKeys.length === dimensions.length;

    let matchedDim = null;
    if (isDimsSelected) {
      const { colorStock } = this.state;
      const d1 = selected[sKeys[0]];
      const d2 = selected[sKeys[1]];

      colorStock.forEach(dim => {
        const dv = Object.values(dim);
        // if two dimensions exist, make sure to check both
        if (d2) {
          if (dv.includes(d1) && dv.includes(d2)) {
            matchedDim = dim;
          }
        } else {
          if (dv.includes(d1)) {
            matchedDim = dim;
          }
        }
      });
    }

    this.checkStock(matchedDim, isDimsSelected);
  }

  onAddToCart = (item, event, win = window) => {
    win.scrollTo(0, 0);
    const { addHeartToCart, onClose } = this.props;
    const { asin, stockId } = item;
    const id = asin || stockId;
    addHeartToCart(id);
    onClose();
  };

  getStyleId = () => {
    const { item: { styleId } } = this.props;
    return styleId;
  }

  checkStock = (dim, isDimsSelected) => {
    // if you haven't selected all dimensions, clear stockId from state
    const newState = {
      ...this.state,
      isDimsSelected,
      oos: undefined,
      stockId: undefined
    };

    if (!isDimsSelected) {
      this.setState({
        ...newState
      });
    } else if (!dim) {
      this.setState({
        ...newState,
        oos: true
      });
    } else {
      const { id, onHand } = dim;
      const oos = !!(onHand <= 0);

      this.setState({
        ...newState,
        stockId: id,
        oos
      });
    }
  }

  fetchProductAndAdd = ({ productUrl }) => {
    const { fetchProductDetail } = this.props;

    fetchProductDetail({ asin: getAsinFromProductUrl(productUrl), includeOosSizing: true })
      .then(() => {
        const {
          product: {
            colorId,
            detail: { sizing }
          }
        } = this.props;

        const { stockData } = sizing;

        // filter specific color from stockData
        const colorStock = stockData.find(({ color }) => color === colorId);
        colorStock && this.onAddToCart({ asin: colorStock.id });
      });
  }

  makeCTAButton = () => {
    const { item } = this.props;
    const { testId } = this.context;

    const {
      oneSize,
      quantity,
      asin
    } = item;

    const { dimensions, isDimsSelected, stockId, oos } = this.state;

    const id = asin || stockId;

    // if product is not in stock
    if (!quantity || oos) {
      // return "Out of Stock" for non-oos-notification marketplaces, or products not in the catalog
      return (
        <button
          disabled={true}
          type="button"
          className={css.disabledCTA}
          data-test-id={testId('outOfStock')}>
            Out of stock
        </button>
      );
    }

    // there are no dimensions to choose from, but we still need to make a call to productBundle to get stockId
    if (oneSize) {
      return (
        <button
          type="button"
          className={css.primaryCTA}
          onClick={this.fetchProductAndAdd.bind(this, item)}
          data-test-id={testId('addToCartNoDimension')}>
        Add To Cart
        </button>
      );
    }

    // id means the selected dimension or favorite has an id, and all dimensions have been selected
    if (id || dimensions) {
      const itemWithAsin = {
        ...item,
        asin,
        stockId
      };
      return (
        <button
          type="button"
          disabled={!asin && !isDimsSelected}
          className={css.primaryCTA}
          onClick={this.onAddToCart.bind(this, itemWithAsin)}
          data-test-id={testId('addToCart')}>
        Add To Cart
        </button>
      );
    }
  }

  makeDimensionDropdown = ({ name, units }) => {
    const { values } = units[0];
    const { testId } = this.context;

    return (
      <select
        name={name}
        onChange={this.onDimensionChange.bind(this)}
        required
        data-test-id={testId(`${name}Dropdown`)}>
        <option value="">Select {name}</option>
        {values.map(({ id, value }) =>
          <option key={id} value={id}>{value}</option>
        )}
      </select>
    );
  }

  makeDimensions = () => {
    const {
      dimensions
    } = this.state;

    // product is a favorite, or product has no dimensions (jewelry, sunglasses, etc)
    if (!(dimensions?.length)) {
      return null;
    }

    return (
      <form method="post" className={css.dimensions}>
        {dimensions.map(dim => {
          const { name } = dim;

          return (
            <Fragment key={name}>
              <label htmlFor={name}>{name}</label>
              {this.makeDimensionDropdown(dim)}
            </Fragment>
          );
        })}
      </form>
    );
  }

  makeImage = ({ productName, imageId }) => {
    const imagePath = constructMSAImageUrl(imageId, msaImageDimensions);
    return <img src={imagePath} alt={productName} />;
  }

  makeBuyBox = () => {
    const { item: { quantity } } = this.props;

    if (!quantity) {
      return <p className={css.oosBox}>This item is out of stock.</p>;
    }

    const {
      item: {
        productUrl,
        productId,
        color,
        price
      },
      product: {
        detail: {
          productRating,
          reviewCount
        } = {}
      }
    } = this.props;

    return (
      <div className={css.buyBox}>
        <span className={css.sku}>sku {productId}</span>
        <p className={css.price}>{toUSD(price)}</p>

        { !!Number(reviewCount) &&
              <div className={css.rating}>
                <Rating
                  additionalClasses={css.stars}
                  rating={productRating}
                  reviewCount={reviewCount}
                  forceDesktopRating={true}
                  fullSize={true}/>
                <Link to={`/product/review/${productId}`}>{reviewCount} {pluralize('review', reviewCount)}</Link>
              </div>
        }

        { color &&
              <dl className={css.color}>
                <dt>Color</dt>
                <dd>{color}</dd>
              </dl>
        }

        { this.makeDimensions() }
        { this.makeCTAButton() }

        <SocialCollectionsWidget getStyleId={this.getStyleId} shouldCollectionRefresh={false} />
        <Link className={css.fullDetails} to={productUrl}>See full details</Link>
      </div>
    );
  }

  render() {
    const { item, onClose } = this.props;

    if (!item) {
      return <Loader />;
    }

    const {
      brandName,
      productName
    } = item;

    return (
      <Modal
        className={cn(modalCSS.modalContent, css.modal)}
        overlayClassName={modalCSS.modalOverlay}
        isOpen={true}>

        <div className={cn(modalCSS.header, css.header)}>
          <h2><strong>{brandName}</strong> {productName}</h2>
          <button
            type="button"
            className={modalCSS.close}
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        <div className={css.container}>
          <div className={css.image}>{this.makeImage(item)}</div>
          { this.makeBuyBox() }
        </div>
      </Modal>
    );
  }
}

export const mapStateToProps = state => ({
  product: state.product
});

export default connect(mapStateToProps, {
  addHeartToCart,
  fetchProductDetail
})(CollectionQuickview);
